import React, { createContext, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import {
  ButtonsWrapper,
  ChartBlock,
  ChartButtonBlock,
  ChartButtons,
  ChartsWrapper,
  LegendBlock,
  LegendRow,
  LegendTitle,
  LoaderWrapper,
} from './Charts.styled'
import { ColorType, createChart, CrosshairMode, IChartApi, ISeriesApi, UTCTimestamp } from 'lightweight-charts'
import { colorsEnum, theme } from '../../../utils/GlobalStyles'
import { IChartTimeInterval, IChartTypes } from '../../../redux/rtkApi/types'
import { Loader } from '../../../utils/Loader/Loader'
import { ColorDatePickerButton, DatePickerButton, HoverWrapper } from '../Actions'
import { CustomSwitch } from '../../../components/UI/CustomSwitch/CustomSwitch'
import { defaultChartTypes } from '../constants'
import { IChartTypesState, TimeIntervalsEnum } from '../types'
import { timeToTz } from '../utils'

const chartColorByName = (name: keyof IChartTypes) => {
  // TODO need to create shades of color by index fore every color that already added
  switch (name) {
    case 'fullVolume':
      return theme.pink
    case 'missIndex':
      return theme.red
    case 'volumeToAction':
      return theme.purple
    case 'profit':
      return theme.green
    case 'count':
      return '#fff'
    case 'feeToPureProfit':
      return theme.yellow
    case 'volumeToPureProfit':
      return theme.lightBlue
    case 'pureProfit':
      return theme.blue
    case 'expectedProfit':
      return theme.orange
    case 'expectedProfitPercent':
      return theme.purple
    case 'avgTimeBetweenOrders':
      return theme.lightBlue
  }
}

export const Charts: React.FC<{
  chartsData:
    | {
        isGeneralChart: false
        byTime: {
          _id: string
          documents: IChartTimeInterval[]
        }[]
        grow: {
          _id: string
          documents: IChartTimeInterval[]
        }[]
      }
    | {
        isGeneralChart: true
        byTime: IChartTimeInterval[]
        grow: IChartTimeInterval[]
      }
  chartTypes: IChartTypesState
  setChartTypes: Dispatch<SetStateAction<IChartTypesState>>
  timeInterval: TimeIntervalsEnum
  setTimeInterval: Dispatch<SetStateAction<TimeIntervalsEnum>>
  showGeneralChart: boolean
  setShowGeneralChart: Dispatch<SetStateAction<boolean>>
  isLoading: boolean
  isFetching: boolean
  showAll: boolean
  isLast24H: boolean
  isLastHour: boolean
}> = ({
  chartsData,
  chartTypes,
  setChartTypes,
  timeInterval,
  setTimeInterval,
  showGeneralChart,
  setShowGeneralChart,
  isLoading,
  isFetching,
  showAll,
  isLast24H,
  isLastHour,
}) => {
  const [somePropChanged, setSomePropChanged] = useState(false)
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chart = useRef<IChartApi>(undefined)
  const [allCharts, setAllCharts] = useState<Map<keyof IChartTypes, ISeriesApi<'Line'>>>(new Map())
  useEffect(() => {
    setSomePropChanged(true)
  }, [timeInterval, isLast24H, isLastHour, showAll])

  useEffect(() => {
    if (!chart?.current) {
      chart.current = createChart(chartContainerRef.current || '', {
        layout: {
          background: { type: ColorType.Solid, color: theme.dark },
          textColor: '#ffffff',
        },
        grid: {
          vertLines: { color: theme.white11 },
          horzLines: { color: theme.white11 },
        },
        height: 600,
        width: chartContainerRef?.current?.clientWidth,
        crosshair: {
          ...(!chartsData.isGeneralChart && {
            mode: CrosshairMode.Normal,
          }),
          vertLine: {
            labelBackgroundColor: theme.yellow,
          },

          horzLine: {
            labelBackgroundColor: theme.yellow,
          },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: true,
        },
      })
    }
    const handleResize = () => {
      chart?.current?.applyOptions({
        width: chartContainerRef?.current?.clientWidth,
      })
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  useEffect(() => {
    if (chartsData.isGeneralChart) {
      Object.entries(chartTypes)
        .filter(([key, value]) => (value.byTime || value.grow) && key !== 'date')
        .forEach(([key, value]) => {
          // const seriesTitle = `${key}`
          if (value.byTime) {
            if (allCharts.has(key as keyof IChartTypes)) {
              const exists = allCharts.get(key as keyof IChartTypes)
              if (exists) {
                exists.setData(
                  [...chartsData.byTime]
                    .sort((prev, next) => prev.date - next.date)
                    .map(timeInterval => ({
                      time: timeToTz(timeInterval.date) as UTCTimestamp,
                      // time: Math.floor(timeInterval.date / 1000) as UTCTimestamp,
                      value: timeInterval[key as keyof IChartTypes],
                    }))
                )
              }
            } else {
              const lineSeries = chart?.current?.addLineSeries({
                color: chartColorByName(key as keyof IChartTypes),
                priceLineVisible: false,
                lastValueVisible: false,
              })
              if (lineSeries) allCharts.set(key as keyof IChartTypes, lineSeries)
              lineSeries?.setData(
                [...chartsData.byTime]
                  .sort((prev, next) => prev.date - next.date)
                  .map(timeInterval => ({
                    time: timeToTz(timeInterval.date) as UTCTimestamp,
                    // time: Math.floor(timeInterval.date / 1000) as UTCTimestamp,
                    value: timeInterval[key as keyof IChartTypes],
                  }))
              )
            }
          }
          if (value.grow) {
            //
          }
        })
    } else {
      //
    }

    if (somePropChanged && !isLoading) {
      chart?.current?.timeScale().fitContent()
      setSomePropChanged(false)
    }
  }, [chartsData, chartTypes, timeInterval, isLast24H, isLastHour, showAll])

  return (
    <ChartsWrapper>
      <ChartButtons>
        <ButtonsWrapper>
          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.feeBlue}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.fee.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.fee
                  if (prevItem.byTime) {
                    const series = allCharts.get('fee')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('fee')
                    }
                  }
                  return {
                    ...prev,
                    fee: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              FEE
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.white}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.count.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.count
                  if (prevItem.byTime) {
                    const series = allCharts.get('count')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('count')
                    }
                  }
                  return {
                    ...prev,
                    count: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              COUNT
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.green}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.profit.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.profit
                  if (prevItem.byTime) {
                    const series = allCharts.get('profit')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('profit')
                    }
                  }
                  return {
                    ...prev,
                    profit: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              PROFIT
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.pink}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.fullVolume.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.fullVolume
                  if (prevItem.byTime) {
                    const series = allCharts.get('fullVolume')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('fullVolume')
                    }
                  }
                  return {
                    ...prev,
                    fullVolume: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              FULL VOL
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.blue}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.pureProfit.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.pureProfit
                  if (prevItem.byTime) {
                    const series = allCharts.get('pureProfit')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('pureProfit')
                    }
                  }
                  return {
                    ...prev,
                    pureProfit: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              PURE PROF
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.purple}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.volumeToAction.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.volumeToAction
                  if (prevItem.byTime) {
                    const series = allCharts.get('volumeToAction')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('volumeToAction')
                    }
                  }
                  return {
                    ...prev,
                    volumeToAction: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              VOL TO ACT
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.red}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.missIndex.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.missIndex
                  if (prevItem.byTime) {
                    const series = allCharts.get('missIndex')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('missIndex')
                    }
                  }
                  return {
                    ...prev,
                    missIndex: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              MISS INDEX
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.lightBlue}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.volumeToPureProfit.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.volumeToPureProfit
                  if (prevItem.byTime) {
                    const series = allCharts.get('volumeToPureProfit')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('volumeToPureProfit')
                    }
                  }
                  return {
                    ...prev,
                    volumeToPureProfit: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              VOL TO PURE PROF
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.yellow}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.feeToPureProfit.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.feeToPureProfit
                  if (prevItem.byTime) {
                    const series = allCharts.get('feeToPureProfit')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('feeToPureProfit')
                    }
                  }
                  return {
                    ...prev,
                    feeToPureProfit: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              FEE TO PURE PROF
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.orange}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.expectedProfit.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.expectedProfit
                  if (prevItem.byTime) {
                    const series = allCharts.get('expectedProfit')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('expectedProfit')
                    }
                  }
                  return {
                    ...prev,
                    expectedProfit: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              SUM EXP PROFIT
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.purple}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.expectedProfitPercent.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.expectedProfitPercent
                  if (prevItem.byTime) {
                    const series = allCharts.get('expectedProfitPercent')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('expectedProfitPercent')
                    }
                  }
                  return {
                    ...prev,
                    expectedProfitPercent: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              AVG EXP PROFIT %
            </ColorDatePickerButton>
          </HoverWrapper>

          <HoverWrapper>
            <ColorDatePickerButton
              color={colorsEnum.lightBlue}
              style={{ fontSize: '10px' }}
              isActive={chartTypes.avgTimeBetweenOrders.byTime}
              onClick={() => {
                setChartTypes(prev => {
                  const prevItem = prev.avgTimeBetweenOrders
                  if (prevItem.byTime) {
                    const series = allCharts.get('avgTimeBetweenOrders')
                    if (series) {
                      chart.current?.removeSeries(series)
                      allCharts.delete('avgTimeBetweenOrders')
                    }
                  }
                  return {
                    ...prev,
                    avgTimeBetweenOrders: {
                      byTime: !prevItem.byTime,
                      grow: prevItem.grow,
                    },
                  }
                })
              }}
            >
              AVG TIME
            </ColorDatePickerButton>
          </HoverWrapper>

          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '30px' }}>
            {Object.entries(TimeIntervalsEnum).map(([_key, value]) => (
              <DatePickerButton isActive={timeInterval === value} onClick={() => setTimeInterval(value)} style={{ marginTop: '3px' }}>
                {value}
              </DatePickerButton>
            ))}
            <ChartButtonBlock colColor={'#fff'}>
              GENERAL INFO
              <CustomSwitch value={showGeneralChart} setValue={setShowGeneralChart} />
            </ChartButtonBlock>
          </div>
        </ButtonsWrapper>
        <LoaderWrapper>{(isLoading || isFetching) && <Loader />}</LoaderWrapper>
      </ChartButtons>

      <ChartBlock ref={chartContainerRef}>
        <LegendBlock>
          {Object.entries(chartTypes)
            .filter(([_, value]) => value.byTime || value.grow)
            .map(([key, _]) => (
              <LegendRow>
                <LegendTitle colorByKey={chartColorByName(key as keyof IChartTypes) || ''}>{key}</LegendTitle>
              </LegendRow>
            ))}
        </LegendBlock>
      </ChartBlock>
    </ChartsWrapper>
  )
}
